import { useCallback, useEffect, useMemo, useRef, useState, type FormEvent, type KeyboardEvent, type ReactNode } from "react";
import {
  api, ApiError,
  type ChatMessage, type ChatSuggestedAction, type ChatToolCall, type ProductRow,
} from "../api";
import type { Route } from "../router";
import { getSession } from "../session";
import { bustAsyncCache } from "../useAsync";
import { track } from "../lib/analytics";
import { KeeperMark } from "./KeeperMark";

const STARTERS: Record<string, string[]> = {
  dashboard: [
    "What's paying back this month?",
    "Create a marketing dashboard",
    "What should I vibe-code next?",
  ],
  attribution: [
    "What's my best payback channel?",
    "How much revenue is unattributed?",
  ],
  funnel: [
    "Analyze funnel dropoffs",
    "Propose a tighter funnel definition",
  ],
  pages: [
    "Which landing pages are leaking?",
    "What should I vibe-code next?",
  ],
  demand: [
    "Which search queries are striking distance?",
    "How is social engagement trending?",
  ],
  health: [
    "What's broken in tracking?",
    "Set up first-party tracking",
  ],
  history: ["Summarise the last week of decisions"],
  integrations: [
    "What sources should I connect first?",
    "Set up first-party tracking",
  ],
  settings: ["What's on my plan, and what am I missing?"],
};

const DEFAULT_STARTERS = [
  "What's paying back this month?",
  "What should I vibe-code next?",
  "Set up first-party tracking",
  "Create a marketing dashboard",
];

type ThreadMessage = ChatMessage & {
  tools?: ChatToolCall[];
  actions?: ChatSuggestedAction[];
};

function storageKey(accountId: string, slug: string | null): string {
  return `fk_chat:${accountId}:${slug ?? "_"}`;
}

function loadThread(accountId: string, slug: string | null): ThreadMessage[] {
  try {
    const raw = localStorage.getItem(storageKey(accountId, slug));
    if (!raw) return [];
    const parsed = JSON.parse(raw) as ThreadMessage[];
    return Array.isArray(parsed) ? parsed.slice(-24) : [];
  } catch {
    return [];
  }
}

function saveThread(accountId: string, slug: string | null, messages: ThreadMessage[]): void {
  try {
    localStorage.setItem(storageKey(accountId, slug), JSON.stringify(messages.slice(-24)));
  } catch { /* quota */ }
}

function sectionOf(route: Route): string {
  if (route.name === "product") return route.section;
  if (route.name === "integrations") return "integrations";
  if (route.name === "settings") return "settings";
  return "dashboard";
}

export function keeperHotkeyLabel(): string {
  return typeof navigator !== "undefined" && /Mac|iPhone|iPad/.test(navigator.platform) ? "⌘K" : "Ctrl+K";
}

// The panel is docked open by default. Below the sidebar breakpoint it is a
// full-screen overlay instead, which is not a state to land on.
export function keeperChatStartsOpen(): boolean {
  if (typeof window === "undefined") return false;
  return !window.matchMedia("(max-width: 860px)").matches;
}

export function KeeperChat({
  open,
  onOpenChange,
  product,
  route,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  product: ProductRow | null;
  route: Route;
}) {
  const accountId = getSession()?.account.id ?? "anon";
  const slug = product?.slug ?? null;
  const section = sectionOf(route);
  const [messages, setMessages] = useState<ThreadMessage[]>(() => loadThread(accountId, slug));
  const [draft, setDraft] = useState("");
  const [busy, setBusy] = useState(false);
  const [liveTools, setLiveTools] = useState<ChatToolCall[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState<string | null>(null);
  const scroller = useRef<HTMLDivElement>(null);
  const input = useRef<HTMLTextAreaElement>(null);
  const mounted = useRef(false);

  useEffect(() => {
    setMessages(loadThread(accountId, slug));
    setError(null);
    setLiveTools([]);
  }, [accountId, slug]);

  useEffect(() => {
    saveThread(accountId, slug, messages);
  }, [accountId, slug, messages]);

  useEffect(() => {
    const el = scroller.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages, liveTools, open]);

  // Focus the composer when someone opens the panel, but not when it simply
  // starts open — that would take the caret on every page load.
  useEffect(() => {
    if (!mounted.current) {
      mounted.current = true;
      return;
    }
    if (open) setTimeout(() => input.current?.focus(), 40);
  }, [open]);

  useEffect(() => {
    const onKey = (e: globalThis.KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        onOpenChange(!open);
      }
      if (e.key === "Escape" && open) {
        e.preventDefault();
        onOpenChange(false);
      }
    };
    addEventListener("keydown", onKey);
    return () => removeEventListener("keydown", onKey);
  }, [open, onOpenChange]);

  const starters = STARTERS[section] ?? DEFAULT_STARTERS;

  const send = useCallback(async (text: string) => {
    const content = text.trim();
    if (!content || busy) return;
    const nextUser: ThreadMessage = { role: "user", content };
    const history = [...messages, nextUser].filter((m) => m.role === "user" || m.role === "assistant");
    setMessages((prev) => [...prev, nextUser]);
    setDraft("");
    setBusy(true);
    setError(null);
    setLiveTools([]);
    track("keeper_chat_sent", { section, has_product: Boolean(slug) });

    const payload: ChatMessage[] = history.map((m) => ({ role: m.role, content: m.content }));
    try {
      const res = await api.chat.stream(
        { slug, messages: payload, section },
        {
          onTool: (e) => {
            setLiveTools((prev) => {
              const i = prev.findIndex((t) => t.name === e.name && t.status === "running");
              const row: ChatToolCall = { name: e.name, status: e.status, label: e.label, error: e.error };
              if (i >= 0) {
                const copy = prev.slice();
                copy[i] = row;
                return copy;
              }
              return [...prev, row];
            });
          },
        },
      );
      setMessages((prev) => [...prev, {
        role: "assistant",
        content: res.reply,
        tools: res.tool_calls,
        actions: res.suggested_actions,
      }]);
      setLiveTools([]);
    } catch (e) {
      const msg = e instanceof ApiError ? e.message : String(e);
      setError(msg);
      setLiveTools([]);
    } finally {
      setBusy(false);
    }
  }, [busy, messages, section, slug]);

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    void send(draft);
  };

  const onKey = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      void send(draft);
    }
  };

  const act = async (action: ChatSuggestedAction) => {
    if (action.kind === "navigate" && action.href) {
      location.hash = action.href;
      return;
    }
    if (action.kind === "copy" && action.text) {
      try {
        await navigator.clipboard.writeText(action.text);
        setCopied(action.label);
        window.setTimeout(() => setCopied(null), 1600);
      } catch { /* ignore */ }
      return;
    }
    if ((action.kind === "approve" || action.kind === "reject") && action.card_id) {
      try {
        if (action.kind === "approve") await api.approve(action.card_id);
        else await api.reject(action.card_id);
        bustAsyncCache();
        setMessages((prev) => [...prev, {
          role: "assistant",
          content: action.kind === "approve" ? "Approved. Recorded against your login." : "Dismissed.",
        }]);
      } catch (e) {
        setError(e instanceof ApiError ? e.message : String(e));
      }
    }
  };

  const clear = () => {
    setMessages([]);
    saveThread(accountId, slug, []);
    setError(null);
  };

  return (
    <>
      <div
        className={`keeper-chat-backdrop${open ? " is-open" : ""}`}
        hidden={!open}
        onClick={() => onOpenChange(false)}
      />
      <aside className={`keeper-chat${open ? " is-open" : ""}`} aria-hidden={!open} aria-label="Ask the Keeper">
        <header className="keeper-chat-head">
          <div className="keeper-chat-title">
            <KeeperMark size={16} />
            <div>
              <div className="keeper-chat-name">Ask the Keeper</div>
              <div className="keeper-chat-sub">
                {product ? product.name : "Workspace"}
                {section !== "dashboard" ? ` · ${section}` : ""}
              </div>
            </div>
          </div>
          <div className="keeper-chat-head-actions">
            {messages.length > 0 && (
              <button type="button" className="btn-text" onClick={clear}>Clear</button>
            )}
            <button type="button" className="btn-text" onClick={() => onOpenChange(false)} aria-label="Close chat">
              Close
            </button>
          </div>
        </header>

        <div className="keeper-chat-thread" ref={scroller}>
          {messages.length === 0 && !busy && (
            <div className="keeper-chat-empty">
              <p>Set up sources, change the dashboard, or ask what to vibe-code next. I read your warehouse — I don't invent figures.</p>
              <div className="keeper-chat-starters">
                {starters.map((s) => (
                  <button key={s} type="button" className="keeper-chat-starter" onClick={() => void send(s)}>
                    {s}
                  </button>
                ))}
              </div>
            </div>
          )}
          {messages.map((m, i) => (
            <div key={i} className={`keeper-chat-msg keeper-chat-msg-${m.role}`}>
              {m.role === "assistant" && <span className="keeper-chat-who"><KeeperMark size={12} /> Keeper</span>}
              {m.tools && m.tools.length > 0 && <ToolPills tools={m.tools} />}
              <div className="keeper-chat-bubble">
                {m.role === "assistant" ? <RichText text={m.content} /> : m.content}
              </div>
              {m.actions && m.actions.length > 0 && (
                <div className="keeper-chat-actions">
                  {m.actions.map((a, j) => (
                    <button
                      key={`${a.kind}-${j}`}
                      type="button"
                      className={a.kind === "approve" ? "btn-accent" : "btn-ghost"}
                      onClick={() => void act(a)}
                    >
                      {copied === a.label ? "Copied" : a.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
          ))}
          {busy && liveTools.length > 0 && <ToolPills tools={liveTools} />}
          {busy && liveTools.length === 0 && (
            <div className="keeper-chat-thinking">Keeper is reading…</div>
          )}
          {error && <div className="keeper-chat-error">{error}</div>}
        </div>

        <form className="keeper-chat-composer" onSubmit={onSubmit}>
          <textarea
            ref={input}
            rows={2}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={onKey}
            placeholder="Ask to set up, change, or explain…"
            disabled={busy}
          />
          <button type="submit" className="btn-accent" disabled={busy || !draft.trim()}>
            Send
          </button>
        </form>
      </aside>
    </>
  );
}

function ToolPills({ tools }: { tools: ChatToolCall[] }) {
  return (
    <div className="keeper-chat-tools">
      {tools.map((t, i) => (
        <span key={`${t.name}-${i}`} className={`keeper-chat-tool is-${t.status}`} title={t.error}>
          {t.status === "running" ? "…" : t.status === "error" ? "!" : "✓"} {t.label}
        </span>
      ))}
    </div>
  );
}

export function KeeperChatTrigger({ onClick }: { onClick: () => void }) {
  return (
    <button type="button" className="btn-text keeper-chat-trigger" onClick={onClick}>
      Ask Keeper <kbd>{keeperHotkeyLabel()}</kbd>
    </button>
  );
}

export function KeeperChatFab({ onClick }: { onClick: () => void }) {
  return (
    <button type="button" className="keeper-chat-fab" onClick={onClick} aria-label="Ask the Keeper">
      <KeeperMark size={18} />
    </button>
  );
}

function RichText({ text }: { text: string }) {
  const blocks = useMemo(() => splitFences(text), [text]);
  return (
    <>
      {blocks.map((b, i) => b.type === "code"
        ? <pre key={i} className="keeper-chat-code"><code>{b.text}</code></pre>
        : <p key={i} className="keeper-chat-prose">{inline(b.text)}</p>)}
    </>
  );
}

function splitFences(text: string): Array<{ type: "text" | "code"; text: string }> {
  const parts = text.split(/```[\w-]*\n?/);
  return parts.map((p, i) => ({ type: i % 2 === 1 ? "code" as const : "text" as const, text: p.trim() }))
    .filter((p) => p.text.length > 0);
}

function inline(text: string): ReactNode[] {
  const nodes: ReactNode[] = [];
  const re = /(\*\*[^*]+\*\*|`[^`]+`|\[[^\]]+\]\([^)]+\))/g;
  let last = 0;
  let m: RegExpExecArray | null;
  let k = 0;
  while ((m = re.exec(text))) {
    if (m.index > last) nodes.push(text.slice(last, m.index));
    const tok = m[0];
    if (tok.startsWith("**")) nodes.push(<strong key={k++}>{tok.slice(2, -2)}</strong>);
    else if (tok.startsWith("`")) nodes.push(<code key={k++}>{tok.slice(1, -1)}</code>);
    else {
      const link = tok.match(/^\[([^\]]+)\]\(([^)]+)\)$/);
      if (link) {
        const href = link[2];
        if (href.startsWith("#/")) {
          nodes.push(
            <button key={k++} type="button" className="btn-link" onClick={() => { location.hash = href; }}>
              {link[1]}
            </button>,
          );
        } else {
          nodes.push(<a key={k++} href={href} target="_blank" rel="noreferrer">{link[1]}</a>);
        }
      }
    }
    last = m.index + tok.length;
  }
  if (last < text.length) nodes.push(text.slice(last));
  return nodes;
}
